import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { platformIcon, PLATFORM_ACCENTS } from "../../lib/platforms";
import { CheckCircle2, Link2, Settings2, X, Loader2, RefreshCw } from "lucide-react";
import { HealthBadge } from "../../components/admin/HealthBadge";

const STATUS_LABEL = {
    ready: { text: "Always on", cls: "bg-emerald-500/15 text-emerald-300" },
    connected: { text: "Connected", cls: "bg-emerald-500/15 text-emerald-300" },
    setup_required: { text: "Setup required", cls: "bg-blue-500/15 text-sky-400" },
};

const META_KEYS = ["facebook"];
const OAUTH_KEYS = ["facebook", "instagram", "linkedin"];

export default function Channels() {
    const [channels, setChannels] = useState([]);
    const [info, setInfo] = useState(null);
    const [connecting, setConnecting] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    const load = useCallback(
        () => api.get("/admin/social/channels").then((r) => setChannels(r.data)),
        []
    );
    const [health, setHealth] = useState({});
    const [checking, setChecking] = useState(false);
    const checkHealth = useCallback(() => {
        setChecking(true);
        return api
            .get("/admin/social/health")
            .then((r) => setHealth(Object.fromEntries(r.data.map((h) => [h.platform, h]))))
            .catch(() => toast.error("Couldn't run the connection check"))
            .finally(() => setChecking(false));
    }, []);
    useEffect(() => {
        load();
        checkHealth();
    }, [load, checkHealth]);

    // Handle the return from Meta's OAuth screen
    useEffect(() => {
        const meta = searchParams.get("meta");
        const insta = searchParams.get("instagram");
        if (!meta && !insta && !searchParams.get("linkedin")) return;
        if (meta) {
            const msg = {
                connected: ["success", "Facebook connected!"],
                denied: ["error", "Connection was cancelled."],
                nopage: ["error", "No Facebook Page was found on that account."],
                error: ["error", "Couldn't connect. Please try again."],
            }[meta];
            if (msg) toast[msg[0]](msg[1]);
            searchParams.delete("meta");
        }
        if (insta) {
            const msg = {
                connected: ["success", "Instagram connected!"],
                denied: ["error", "Instagram connection was cancelled."],
                error: ["error", "Couldn't connect Instagram. Please try again."],
            }[insta];
            if (msg) toast[msg[0]](msg[1]);
            searchParams.delete("instagram");
        }
        const li = searchParams.get("linkedin");
        if (li) {
            const msg = {
                connected: ["success", "LinkedIn connected!"],
                denied: ["error", "LinkedIn connection was cancelled."],
                error: ["error", "Couldn't connect LinkedIn. Please try again."],
            }[li];
            if (msg) toast[msg[0]](msg[1]);
            searchParams.delete("linkedin");
        }
        setSearchParams(searchParams, { replace: true });
        load();
        checkHealth();
    }, [searchParams, setSearchParams, load, checkHealth]);

    const connectMeta = async () => {
        setConnecting(true);
        try {
            const { data } = await api.get("/meta/oauth/start");
            window.location.assign(data.url);
        } catch (e) {
            toast.error(apiError(e.response?.data?.detail) || "Could not start connection");
            setConnecting(false);
        }
    };

    const connectInstagram = async () => {
        setConnecting(true);
        try {
            const { data } = await api.get("/instagram/oauth/start");
            window.location.assign(data.url);
        } catch (e) {
            toast.error(apiError(e.response?.data?.detail) || "Could not start connection");
            setConnecting(false);
        }
    };

    const connectLinkedIn = async () => {
        setConnecting(true);
        try {
            const { data } = await api.get("/linkedin/oauth/start");
            window.location.assign(data.url);
        } catch (e) {
            toast.error(apiError(e.response?.data?.detail) || "Could not start connection");
            setConnecting(false);
        }
    };

    const disconnectMeta = async () => {
        try {
            await api.delete("/admin/social/meta");
            toast.success("Disconnected");
            load();
        } catch (e) {
            toast.error(apiError(e.response?.data?.detail) || "Could not disconnect");
        }
    };

    const disconnectInstagram = async () => {
        try {
            await api.delete("/admin/social/instagram");
            toast.success("Disconnected");
            load();
        } catch (e) {
            toast.error(apiError(e.response?.data?.detail) || "Could not disconnect");
        }
    };

    const disconnectLinkedIn = async () => {
        try {
            await api.delete("/admin/social/linkedin");
            toast.success("Disconnected");
            load();
        } catch (e) {
            toast.error(apiError(e.response?.data?.detail) || "Could not disconnect");
        }
    };

    const onConnect = (c) => {
        if (c.key === "facebook") connectMeta();
        else if (c.key === "instagram") connectInstagram();
        else if (c.key === "linkedin") connectLinkedIn();
        else setInfo(c);
    };

    const onDisconnect = (key) => {
        if (key === "instagram") disconnectInstagram();
        else if (key === "linkedin") disconnectLinkedIn();
        else disconnectMeta();
    };

    return (
        <div data-testid="admin-channels">
            <div>
                <h1 className="font-serif text-3xl text-blue-50">Channels</h1>
                <p className="mt-1 max-w-2xl text-sm text-slate-300/55">
                    Your posting hub. Connect each network once, then write a single post
                    and send it everywhere from the{" "}
                    <span className="text-sky-400">Create Post</span> screen.
                </p>
                <button
                    data-testid="channels-recheck-btn"
                    onClick={checkHealth}
                    disabled={checking}
                    className="mt-4 inline-flex items-center gap-2 rounded-full border border-blue-500/30 px-4 py-2 text-xs font-semibold text-slate-300 transition-colors hover:bg-blue-500/10 disabled:opacity-60"
                >
                    <RefreshCw className={`h-3.5 w-3.5 ${checking ? "animate-spin" : ""}`} />
                    {checking ? "Checking connections…" : "Re-check connections"}
                </button>
            </div>

            <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {channels.map((c) => {
                    const Icon = platformIcon(c.key);
                    const accent = PLATFORM_ACCENTS[c.key] || "text-sky-400";
                    const badge = STATUS_LABEL[c.status] || STATUS_LABEL.setup_required;
                    return (
                        <div
                            key={c.key}
                            data-testid={`channel-card-${c.key}`}
                            className="flex flex-col rounded-2xl border border-blue-500/15 bg-[#0c172e] p-5"
                        >
                            <div className="flex items-start justify-between">
                                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-500/10 ring-1 ring-blue-500/25">
                                    <Icon className={`h-5 w-5 ${accent}`} />
                                </span>
                                <span
                                    className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${badge.cls}`}
                                >
                                    {badge.text}
                                </span>
                            </div>
                            <h3 className="mt-4 font-serif text-lg text-slate-300">
                                {c.name}
                            </h3>
                            {c.account_name && (
                                <p className="text-xs text-slate-300/50">{c.account_name}</p>
                            )}
                            {c.connected && (
                                <HealthBadge
                                    platform={c.key}
                                    health={health[c.key]}
                                    checking={checking}
                                />
                            )}
                            <p className="mt-2 flex-1 text-xs leading-relaxed text-slate-300/45">
                                {c.note}
                            </p>

                            <div className="mt-4">
                                {c.key === "website" ? (
                                    <div className="flex items-center gap-2 text-xs text-emerald-300/90">
                                        <CheckCircle2 className="h-4 w-4" /> Ready to publish
                                    </div>
                                ) : c.connected ? (
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="flex items-center gap-2 text-xs text-emerald-300/90">
                                            <CheckCircle2 className="h-4 w-4" /> Connected
                                        </span>
                                        {OAUTH_KEYS.includes(c.key) && (
                                            <button
                                                data-testid={`channel-disconnect-${c.key}`}
                                                onClick={() => onDisconnect(c.key)}
                                                className="text-xs text-slate-300/50 hover:text-red-300"
                                            >
                                                Disconnect
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <button
                                        data-testid={`channel-connect-${c.key}`}
                                        onClick={() => onConnect(c)}
                                        disabled={connecting && OAUTH_KEYS.includes(c.key)}
                                        className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 px-4 py-2 text-xs font-semibold text-slate-300 hover:bg-blue-500/10 disabled:opacity-60"
                                    >
                                        {connecting && OAUTH_KEYS.includes(c.key) ? (
                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        ) : (
                                            <Link2 className="h-3.5 w-3.5" />
                                        )}
                                        Connect
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {info && (
                <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/60 p-6">
                    <div className="my-16 w-full max-w-md rounded-2xl border border-blue-500/20 bg-[#0c172e] p-7">
                        <div className="flex items-center justify-between">
                            <h2 className="flex items-center gap-2 font-serif text-2xl text-blue-50">
                                <Settings2 className="h-5 w-5 text-blue-400" /> Connect{" "}
                                {info.name}
                            </h2>
                            <button
                                data-testid="channel-info-close"
                                onClick={() => setInfo(null)}
                                className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400/70 hover:bg-blue-500/15"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <p className="mt-4 text-sm leading-relaxed text-slate-300/70">
                            {info.note}
                        </p>
                        <p className="mt-4 rounded-xl border border-blue-500/15 bg-[#070e1c] p-4 text-sm leading-relaxed text-slate-300/60">
                            The one-time setup for <b className="text-slate-400">{info.name}</b>{" "}
                            is being prepared. Once its developer app is ready, this button will
                            open a secure login so you can link your account in a couple of clicks
                            — no technical steps on your end.
                        </p>
                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={() => setInfo(null)}
                                className="rounded-full bg-gradient-to-b from-blue-400 via-blue-500 to-blue-700 px-6 py-2.5 text-sm font-semibold text-[#ffffff] hover:brightness-110"
                            >
                                Got it
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
